import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { percomaticLoadedState } from '../../../atoms';
import {
	cartQuantityState,
	currentTransactionState,
	sessionCartState
} from '../../../Cart/atoms';
import { Totals } from '../../../Cart/components/Totals';
import { Skeleton as ItemsSkeleton } from '../../../Cart/components/Items/Skeleton';
import { Products } from '../../../Cart/components/Products';
import { Skeleton as TotalsSkeleton } from '../../../Cart/components/Totals/Skeleton';
import { Divider } from '../../../components/Divider';
import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { BalanceDue } from '../BalanceDue';
import { Heading } from '../Heading';
import { Flex } from '../../../GlobalComponents/Flex';

export type CartProps = {
	editable?: boolean;
};

export const OrderSummary: React.FC<CartProps> = () => {
	const { items, subTotal, taxAmount, total } = useRecoilValue(
		currentTransactionState
	);

	const balanceDue = useAppSelector(
		state => state.checkoutReducer.balanceDue
	);

	const selectedCards = useAppSelector(
		state => state.checkoutReducer.giftCardPayments
	);
	const dispatch = useAppDispatch();

	const percomaticLoaded = useRecoilValue(percomaticLoadedState);
	const cartQuantity = useRecoilValue(cartQuantityState);
	const sessionCart = useRecoilValue(sessionCartState);

	useEffect(() => {
		const cardsAreSelected = selectedCards.length !== 0;
		if (cardsAreSelected) {
			return;
		}
	}, [dispatch, selectedCards, total]);

	if (percomaticLoaded && cartQuantity === 0) {
		return <Navigate to="/" replace />;
	}

	return (
		<Flex id="order-summary" $flexDirection="column">
			<Heading $fontSize="24px">Order Summary</Heading>
			<ProductContainer>
				{percomaticLoaded ? (
					<Products key={`Items${sessionCart.id}`} items={items} />
				) : (
					<ItemsSkeleton
						key={`ItemsSkelton${sessionCart.id}`}
						numItems={sessionCart.items.length}
						divider={<Divider my={2} borderColor="light" />}
						editable
					/>
				)}
			</ProductContainer>

			{percomaticLoaded ? (
				<Totals
					subTotal={subTotal}
					total={total}
					taxAmount={taxAmount}
				/>
			) : (
				<TotalsSkeleton />
			)}

			<BalanceDue balanceDue={balanceDue} />
		</Flex>
	);
};

const ProductContainer = styled.div`
	padding-bottom: 16px;
`;
